import {compact} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {mapObject} from 'underscore';

import * as analytics from 'analytics/Analytics';
import {makeAnalyticsEventId} from 'analytics/Analytics';
import {attachArchivePurpose} from 'archive/ArchivePurposesUtil';
import {assert, assertAndContinue} from 'Asserts';
import {DataModificationsHeader} from 'audit/DataModifications';
import {ProcessesFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import BenchlingConfig from 'BenchlingConfig';
import * as benchlingUtil from 'BenchlingUtil';
import {
  ActionMethod,
  ActionOrigin,
  ANALYTICS_MODULE,
  WORKFLOWS_ANALYTICS_ACTIONS,
} from 'constants/AnalyticsConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {
  ProcessExecutionType,
  ProcessPlatformFeatureType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getAccessibleOrInaccessibleId} from 'graphql-client/InaccessibleInfo';
import navigateToURL from 'lib/navigateToURL';
import BenchLink from 'link-preview/BenchLink';
import AliasField from 'metadata/AliasField';
import {getRowAwareLoadingMessage} from 'notebook/structured-tables/action-functions/makeSubmitRowActionFunction';
import {StructuredTableEditStatus} from 'notebook/structured-tables/action-functions/toggle-edit-mode/reduceToggledTableEditState';
import {setStructuredTableTogglableStatus} from 'notebook/structured-tables/action-functions/toggle-edit-mode/StructuredTableTogglableStateActions';
import {
  FetchSelectorsByIdQuery,
  FetchSelectorsByIdQueryVariables,
} from 'notebook/structured-tables/hooks/__generated/FetchSelectorsByIdQuery';
import {
  FETCH_SELECTORS_BY_ID_QUERY,
  getSelectorsById,
} from 'notebook/structured-tables/hooks/useSelectorsById';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {
  LastSyncedUpstreamValue,
  StructuredTableKind,
} from 'notebook/structured-tables/StructuredTableConstants';
import ToggleEditStructuredTableOutsideNotebook from 'notebook/structured-tables/ToggleEditStructuredTableOutsideNotebook';
import {getStructuredTableCellDiffOnSubmission} from 'notebook/structured-tables/util/datamods/getStructuredTableCellDiffOnSubmission';
import rowHasUnsubmittedChanges from 'notebook/structured-tables/util/rowHasUnsubmittedChanges';
import {getSourceTypeForStructuredTable} from 'notebook/structured-tables/util/StructuredTableAnalyticsUtil';
import doesEvaluatedTableHaveAnyErrors from 'notebook/tables/errors/doesEvaluatedTableHaveAnyErrors';
import {EvaluatedTable} from 'notebook/tables/excess/Evaluator';
import {StructuredTableViewPanel} from 'notebook/tables/table-views/TableViewPanel';
import BatchSubmitOutputsTaskUpdateManager from 'notebook/util/BatchSubmitOutputsTaskUpdateManager';
import {getAllBatchRanges} from 'notebook/util/PerformActionFunctions';
import notifications from 'notifications';
import {makeClientActionId} from 'processes/dao/ProcessManagementDAO';
import {asyncBulkUpdateProcessWithDataMods} from 'processes/dashboard/bulk-edit/ProcessTaskEditorDropdown';
import {isDirectExecutionTaskWithNoOutputSchema} from 'processes/dashboard/ExecuteTasksButton';
import {getFailedMappingTasksFilteredByProcessesUrl} from 'processes/dashboard/getModelFilteredByProcessesrUrl';
import MappingInProgressIndicator from 'processes/graph-builder-processes/flowchart-viz/MappingInProgressIndicator';
import {
  DefaultTaskGroupInstructionsLines,
  EVALUATED_TABLE_ERROR_OPTIONS,
  ProcessTableLocation,
} from 'processes/ProcessConstants';
import {processExecutionTypeDisplayName} from 'processes/ProcessConstants';
import {getProcessPlatformFeatureConfig} from 'processes/processes-platform/FeatureOptions';
import ProcessRealtimeUpdateSubscriptionWrapper from 'processes/ProcessRealtimeUpdateSubscriptionWrapper';
import RequestV2SubmissionChip from 'processes/requests_v2/chips/RequestV2SubmissionChip';
import TaskStatusSummary from 'processes/status/TaskStatusSummary';
import createTaskTable from 'processes/tables/createTaskTable';
import getDataModsCountForTableSubmission from 'processes/tables/getDataModsCountForTableSubmission';
import {outputTableToOutputsForCreateOrUpdate} from 'processes/tables/outputTableToOutputsUtils';
import ProcessOutputStructuredTableAdapter, {
  ProcessOutputStructuredTable,
} from 'processes/tables/ProcessOutputStructuredTableAdapter';
import ProcessTaskStructuredTableAdapter, {
  getLastSyncedUpstreamTaskStatus,
  getTaskSchema,
  ProcessTaskStructuredTable,
} from 'processes/tables/ProcessTaskStructuredTableAdapter';
import setRowsInTaskTable from 'processes/tables/setRowsInTaskTable';
import submitOutputsWithConfirmation from 'processes/tables/submitOutputsWithConfirmation';
import submitTasksWithConfirmation from 'processes/tables/submitTasksWithConfirmation';
import taskTableToTasks from 'processes/tables/taskTableToTasks';
import updateOutputTable from 'processes/tables/updateOutputTable';
import {TaskGroupInstructions} from 'processes/TaskGroupInstructions';
import {itemCountsForChangeSummary} from 'samples/getInventoryRegistryChangeSummary';
import {Schema} from 'schemas/SchemaConstants';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Tooltip from 'ui-library/bs/Tooltip';
import {ChipSize} from 'ui-library/chips/Chip';
import {GRAY_DARK} from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import mergeAndSortIdObjectArrays from 'util/mergeAndSortIdObjectArrays';

import {ProcessOutputGraphQL} from '../../../graphql-fragments/processes/__generated/ProcessOutputGraphQL';
import {ProcessTaskGraphQL} from '../../../graphql-fragments/processes/__generated/ProcessTaskGraphQL';
import {SchemaFieldSelectorGraphQL} from '../../../graphql-fragments/schemas/__generated/SchemaFieldSelectorGraphQL';
import {
  bulkCreateOrUpdateProcessOutputs,
  bulkCreateOrUpdateProcessTasks,
  CreateOrUpdateServerProcessOutput,
  CreateOrUpdateServerProcessTask,
  EditExistingTask,
  updateProcess,
  BulkCreateOrUpdateProcessTasksResponse,
} from '../dao/ProcessManagementDAO';
import createOutputTable from '../tables/createOutputTable';
import {ProcessDetailsQuery, ProcessDetailsQueryVariables} from './__generated/ProcessDetailsQuery';
import {PROCESS_DETAILS_QUERY, updateProcessDetailsQueryCache} from './ProcessDetailsQueryUtils';
import {ProcessWatchersField} from './ProcessWatchersField';
import {mergeTasksIntoTaskTable} from './submitOutputs';
import ProcessActionToolbar from './toolbar/ProcessActionToolbar';

type ProcessTask = ExtractByTypename<ProcessDetailsQuery['process'], 'ProcessNode'>['tasks'][number];
type ProcessGql = ExtractByTypename<ProcessDetailsQuery['process'], 'ProcessNode'>;

type RootProcessOrInaccessibleGql = NonNullable<ProcessGql['rootProcess']>;

function hasOutputSchema(process: {processTaskSchema: {outputSchema: Schema | null}}): process is {
  processTaskSchema: {outputSchema: Schema};
} {
  return process.processTaskSchema.outputSchema != null;
}

interface ProcessDetailsProps {
  process: ProcessGql;
  selectors: Array<SchemaFieldSelectorGraphQL>;
  refetch: () => Promise<unknown>;
  isEditable: boolean;
}

interface ProcessNameEditorProps {
  process: ProcessGql;
  isEditable: boolean;
}

function ProcessNameEditor(props: ProcessNameEditorProps): JSX.Element {
  const {process} = props;
  const aliasRef = useRef<AliasField>(null);
  const updateName = async (newName: string): Promise<void> => {
    const oldName = process.name;
    if (oldName !== newName) {
      try {
        await FileBrowserActions.renameFileWithDataMods(process.id, false, {
          oldName,
          newName,
          parentFolderId: process.folder.id,
        });
        analytics.trackEventWithModule(
          WORKFLOWS_ANALYTICS_ACTIONS.UPDATED_TASK_GROUP_NAME,
          ANALYTICS_MODULE.WORKFLOWS,
          {
            action_origin: ActionOrigin.WORKFLOWS_TASK_GROUP_PAGE,
            process_client_origin: process.processTaskSchema.clientOrigin,
            old_value: oldName,
            new_value: newName,
          }
        );
        // eslint-disable-next-line benchlint/always-throw-from-catch
      } catch {
        aliasRef.current?.reset();
      }
    }
  };

  return (
    <div className="metadata-header-section">
      <div className={css(styles.nameWrapper)}>
        <AliasField
          ref={aliasRef}
          initialAlias={props.process.name}
          isFullWidth={false}
          isHeader={true}
          isWritable={
            props.isEditable && isFeatureEnabled({feature: ProcessesFeature.PROCESSES_EDIT, item: process})
          }
          complete={updateName}
          placeholder="Name"
        />
        {(props.process.isMappingInProgressFromProcess || props.process.isMappingInProgressToProcess) && (
          <MappingInProgressIndicator
            text="Mapping in progress..."
            dataTestAttrs={{
              component: 'ProcessDetails',
              element: 'mappingInProgressIndicator',
            }}
          />
        )}
      </div>
    </div>
  );
}

function ProcessDetailsColumn(props: {children: JSX.Element; title: string}): JSX.Element {
  return (
    <Col sm={4}>
      <div className="metadata-section">
        <div className="metadata-header">{props.title}</div>
        {props.children}
      </div>
    </Col>
  );
}

interface RootProcessLinkProps {
  rootProcess: RootProcessOrInaccessibleGql;
}

function RootProcessLink(props: RootProcessLinkProps): JSX.Element {
  const {rootProcess} = props;
  if (isInaccessible(rootProcess)) {
    return (
      <>
        No access
        <Tooltip title="Please contact your administrator for access to the parent task group">
          <BenchlingIcon icon="info-circle" className={css(styles.iconStyle)} />
        </Tooltip>
      </>
    );
  }
  return <BenchLink href={benchlingUtil.pathToURL(rootProcess.editUrl)}>{rootProcess.name}</BenchLink>;
}

export async function submitOutputsAndCorrespondingTasksPostConfirmation({
  process,
  processTasks,
  processOutputs,
  outputTable,
  computedOutputTable,
  taskTable,
  setTaskTable,
  setOutputTable,
  dataModsHeader,
}: {
  process: ProcessGql;
  processTasks?: Array<EditExistingTask>;
  processOutputs: Array<CreateOrUpdateServerProcessOutput>;
  outputTable: ProcessOutputStructuredTable;
  computedOutputTable: EvaluatedTable;
  taskTable: ProcessTaskStructuredTable;
  setTaskTable: (table: ProcessTaskStructuredTable) => void;
  setOutputTable: (table: ProcessOutputStructuredTable) => void;
  dataModsHeader?: DataModificationsHeader;
}): Promise<void> {
  const buildOutputTable = (
    outputTable: ProcessOutputStructuredTable,
    submittedOutputs: Array<ProcessOutputGraphQL>
  ): ProcessOutputStructuredTable =>
    updateOutputTable(
      outputTable,
      submittedOutputs,
      submittedOutputs.map((output) =>
        ProcessOutputStructuredTableAdapter.optimisticallyMakeLastSyncedUpstreamValuesFromOutput(
          output,
          outputTable.columns
        )
      ),
      true
    );

  const setState = (
    newTaskTable: ProcessTaskStructuredTable,
    newOutputTable: ProcessOutputStructuredTable,
    serverTasks: Array<ProcessTask>,
    serverOutputs: Array<ProcessOutputGraphQL>
  ): void => {
    setTaskTable(newTaskTable);
    setOutputTable(newOutputTable);
    updateProcessDetailsQueryCache(process.id, {
      outputs: serverOutputs,
      tasks: serverTasks,
      mergeTasks: true,
      mergeOutputs: true,
    });
    LoadingScreen.hide();
    analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.CLICKED_OUTPUT_TABLE_SUBMIT, {
      process_client_origin: process.processTaskSchema.clientOrigin,
    });
  };

  const outputsToSubmit: Array<SubmissionSpecWithRowIdx<CreateOrUpdateServerProcessOutput>> = compact(
    outputTable.rows.map((row, idx) =>
      rowHasUnsubmittedChanges(computedOutputTable[idx], row, outputTable.columns, [
        process.processTaskSchema.outputSchema!,
      ])
        ? {submission: processOutputs[idx], rowIdx: idx}
        : null
    )
  );
  // We only need to submit tasks that correspond to outputs that are being created
  const tasksThatWillBeSubmitted = processTasks?.filter((task) =>
    outputsToSubmit.some(({submission}) => submission.processTaskId === task.id)
  );
  const allOutputs = [...process.outputs];

  if (processOutputs.length > BenchlingConfig.WORKFLOWS_MAX_ROWS_IN_WORKSPACE_TAB_TABLE_BEFORE_BATCHING) {
    LoadingScreen.show(
      `Saving ${processOutputs.length} ${benchlingUtil.pluralize(processOutputs.length, 'output')}`
    );
    const batchRanges = getAllBatchRanges(
      BenchlingConfig.WORKFLOWS_NUM_ROWS_PER_BATCH_IN_WORKSPACE_TAB_TABLE_SUBMISSION,
      outputsToSubmit.length
    );

    const submittedOutputs: Array<ProcessOutputGraphQL> = [];
    const submittedTasks: Array<ProcessTask> = [];
    const actionId = makeClientActionId();
    const taskUpdateManager = new BatchSubmitOutputsTaskUpdateManager({
      processOutputs: outputsToSubmit.map(({submission}) => submission),
      processTasks: tasksThatWillBeSubmitted,
      willProcessBatchesInOrder: true,
    });
    try {
      for (let i = 0; i < batchRanges.length; i++) {
        const range = batchRanges[i];
        LoadingScreen.set(
          getRowAwareLoadingMessage(
            {
              start: outputsToSubmit[range.start].rowIdx,
              end: outputsToSubmit[range.end - 1].rowIdx + 1,
            },
            'Updating outputs for rows',
            'Updating output for row'
          )
        );
        const tasksToSubmit = taskUpdateManager.getTaskUpdatesForBatch(range);
        const response = await bulkCreateOrUpdateProcessOutputs({
          processOutputs: outputsToSubmit.map(({submission}) => submission).slice(range.start, range.end),
          processTasks: tasksToSubmit,
          showLoadingScreen: false,
          totalOutputsInTable: processOutputs.length,
          batchInfo: {actionId, lastBatch: i + 1 === batchRanges.length},
          dataModsHeader,
        });
        submittedTasks.push(...response.processTasks);
        submittedOutputs.push(...response.processOutputs);
      }
    } catch (e) {
      // If there is an error, we need to preserve the rows that were not updated.
      const currentOutputTable = outputTable;
      const numOutputsSubmitted = submittedOutputs.length;

      const startIdxToPreserve = outputsToSubmit[numOutputsSubmitted].rowIdx;
      const newOutputTable = buildOutputTable(
        outputTable,
        mergeAndSortIdObjectArrays(allOutputs.slice(0, startIdxToPreserve), submittedOutputs)
      );

      const rowsToPreserve = currentOutputTable.rows.slice(startIdxToPreserve);
      newOutputTable.rows.push(...rowsToPreserve);
      submittedOutputs.push(
        ...(startIdxToPreserve < allOutputs.length ? allOutputs.slice(startIdxToPreserve) : [])
      );
      for (let idx = 0; idx < newOutputTable.rows.length; idx++) {
        if (idx < startIdxToPreserve) {
          newOutputTable.rows[idx].isFinalized = true;
        } else {
          newOutputTable.rows[idx].isFinalized = currentOutputTable.rows[idx].isFinalized;
        }
        newOutputTable.rows[idx].lastSyncedUpstreamValues =
          currentOutputTable.rows[idx].lastSyncedUpstreamValues;
      }

      setState(
        mergeTasksIntoTaskTable(taskTable, process, submittedTasks),
        newOutputTable,
        submittedTasks,
        submittedOutputs
      );
      setStructuredTableTogglableStatus(newOutputTable.cid, StructuredTableEditStatus.EDITING);
      throw e;
    }

    const newAllOutputs = mergeAndSortIdObjectArrays(allOutputs, submittedOutputs);
    setState(
      mergeTasksIntoTaskTable(taskTable, process, submittedTasks),
      buildOutputTable(outputTable, newAllOutputs),
      submittedTasks,
      submittedOutputs
    );
  } else {
    const response = await bulkCreateOrUpdateProcessOutputs({
      processOutputs: outputsToSubmit.map(({submission}) => submission),
      processTasks: tasksThatWillBeSubmitted,
      dataModsHeader,
    });

    const newAllOutputs = mergeAndSortIdObjectArrays(allOutputs, response.processOutputs);
    const newOutputTable = buildOutputTable(outputTable, newAllOutputs);
    const newTaskTable = mergeTasksIntoTaskTable(taskTable, process, response.processTasks);

    setState(newTaskTable, newOutputTable, response.processTasks, response.processOutputs);
  }
}

function outputTableToOutputs({
  table,
  evaluatedTable,
  sourceType,
  actionOrigin,
  actionMethod,
  creationOriginId,
}: {
  table: ProcessOutputStructuredTable;
  evaluatedTable: EvaluatedTable;
  sourceType: string;
  actionOrigin: string;
  creationOriginId: string;
  actionMethod: string;
}): Array<CreateOrUpdateServerProcessOutput> {
  const outputs = outputTableToOutputsForCreateOrUpdate({
    table,
    evaluatedTable,
    // Preemptively fill more than necessary to prevent possible out-of-bounds error.
    // In reality, this should only be (the number of rows - outputs to update).
    creationOriginIds: Array(evaluatedTable.length).fill(creationOriginId),
    sourceType,
    actionOrigin,
    actionMethod,
  });
  assertAndContinue(outputs.length > 0, `Expected outputs to submit to be greater than 0.`);
  return outputs;
}

interface LastSyncedUpstreamValuesWithId {
  id: string;
  lastSyncedUpstreamValues: Array<LastSyncedUpstreamValue>;
}

interface SubmissionSpecWithRowIdx<T> {
  rowIdx: number;
  submission: T;
}

export async function submitTaskTableHandler({
  process,
  allTasks,
  taskTable,
  computedTaskTable,
  setTaskTable,
  setAllTasks,
}: {
  process: ProcessGql;
  allTasks: Array<ProcessTask>;
  taskTable: ProcessTaskStructuredTable;
  computedTaskTable: EvaluatedTable;
  setTaskTable: (table: ProcessTaskStructuredTable) => void;
  setAllTasks: (tasks: Array<ProcessTask>) => void;
}): Promise<void> {
  assert((taskTable?.columns?.length ?? 0) > 1, 'Generated task table must have columns');
  if (doesEvaluatedTableHaveAnyErrors(computedTaskTable, EVALUATED_TABLE_ERROR_OPTIONS)) {
    return;
  }
  const tasks = taskTableToTasks({
    table: taskTable,
    evaluatedTable: computedTaskTable,
    processId: process.id,
    sourceType: `workspace_tab_${getSourceTypeForStructuredTable(taskTable)}_direct`,
    actionOrigin: ActionOrigin.WORKFLOWS_TASK_GROUP_WORKFLOW_TASK_TABLE,
    actionMethod: ActionMethod.BUTTON_CLICK,
  }) as Array<CreateOrUpdateServerProcessTask>;

  const setTabState = (newTasks: Array<ProcessTask>, newTaskTable: ProcessTaskStructuredTable): void => {
    setAllTasks(newTasks);
    setTaskTable(newTaskTable);
    updateProcessDetailsQueryCache(process.id, {
      tasks: newTasks,
    });
    LoadingScreen.hide();
    analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.CLICKED_TASK_TABLE_SUBMIT, {
      action_origin: ActionOrigin.WORKFLOWS_TASK_GROUP_WORKFLOW_TASK_TABLE,
      process_client_origin: process.processTaskSchema.clientOrigin,
    });
  };

  const getLastSyncedUpstreamValuesWithIdFromResponse = (
    tasksSubmitted: Array<SubmissionSpecWithRowIdx<CreateOrUpdateServerProcessTask>>,
    response: BulkCreateOrUpdateProcessTasksResponse
  ): Array<LastSyncedUpstreamValuesWithId> =>
    tasksSubmitted.map(({rowIdx}, submissionIdx) => ({
      id: `${rowIdx}`,
      lastSyncedUpstreamValues: response.lastSyncedUpstreamValues[submissionIdx],
    }));

  const getNewTaskData = (
    submittedTasks: Array<ProcessTask>,
    lastSyncedUpstreamValuesWithId: Array<LastSyncedUpstreamValuesWithId>
  ): {
    newAllTasks: Array<ProcessTask>;
    newLastSyncedUpstreamValues: Array<Array<LastSyncedUpstreamValue>>;
  } => {
    const oldLastSyncedUpstreamValues = taskTable.rows.map(({lastSyncedUpstreamValues}, idx) => ({
      id: `${idx}`,
      lastSyncedUpstreamValues: lastSyncedUpstreamValues ?? [],
    }));
    const newAllTasks = mergeAndSortIdObjectArrays(allTasks, submittedTasks);
    const allLastSyncedUpstreamValues = mergeAndSortIdObjectArrays(
      oldLastSyncedUpstreamValues,
      lastSyncedUpstreamValuesWithId
    ).map(({lastSyncedUpstreamValues}) => lastSyncedUpstreamValues);
    return {newAllTasks, newLastSyncedUpstreamValues: allLastSyncedUpstreamValues};
  };

  const tasksToSubmit: Array<SubmissionSpecWithRowIdx<CreateOrUpdateServerProcessTask>> = compact(
    taskTable.rows.map((row, idx) =>
      rowHasUnsubmittedChanges(computedTaskTable[idx], row, taskTable.columns, [process.processTaskSchema])
        ? {rowIdx: idx, submission: tasks[idx]}
        : null
    )
  );

  const onSubmitProcessDetails = async (dataModsHeader?: DataModificationsHeader): Promise<void> => {
    if (tasks.length > BenchlingConfig.WORKFLOWS_MAX_ROWS_IN_WORKSPACE_TAB_TABLE_BEFORE_BATCHING) {
      LoadingScreen.show(
        `Saving ${tasksToSubmit.length} ${benchlingUtil.pluralize(tasksToSubmit.length, 'task')}`
      );
      const batchRanges = getAllBatchRanges(
        BenchlingConfig.WORKFLOWS_NUM_ROWS_PER_BATCH_IN_WORKSPACE_TAB_TABLE_SUBMISSION,
        tasksToSubmit.length
      );
      const lastSyncedUpstreamValues: Array<LastSyncedUpstreamValuesWithId> = [];
      const submittedTasks: Array<ProcessTask> = [];
      const actionId = makeClientActionId();

      try {
        for (let i = 0; i < batchRanges.length; i++) {
          const range = batchRanges[i];
          const submissionBatch = tasksToSubmit.slice(range.start, range.end);
          LoadingScreen.set(
            getRowAwareLoadingMessage(
              {
                start: submissionBatch[0].rowIdx,
                end: submissionBatch[submissionBatch.length - 1].rowIdx + 1,
              },
              'Updating tasks for rows',
              'Updating task for row'
            )
          );
          const response = await bulkCreateOrUpdateProcessTasks({
            tasks: submissionBatch.map(({submission}) => submission),
            showLoadingScreen: false,
            totalTasksInTable: tasks.length,
            batchInfo: {actionId, lastBatch: i + 1 === batchRanges.length},
            dataModsHeader,
          });

          submittedTasks.push(...response.tasks);
          lastSyncedUpstreamValues.push(
            ...getLastSyncedUpstreamValuesWithIdFromResponse(submissionBatch, response)
          );
        }
      } catch (e) {
        const currentTaskTable = taskTable;
        // tasksToSubmit is sorted by the row index so all tasks after the last submitted task have not
        // been submitted yet.
        const numTasksSubmitted = submittedTasks.length;
        const startIdxToPreserve = tasksToSubmit[numTasksSubmitted].rowIdx;

        const tasksToSet = mergeAndSortIdObjectArrays(allTasks.slice(0, startIdxToPreserve), submittedTasks);
        const lastSyncedUpstreamValuesToSet = mergeAndSortIdObjectArrays(
          taskTable.rows.slice(0, startIdxToPreserve).map(({lastSyncedUpstreamValues}, idx) => ({
            id: `${idx}`,
            lastSyncedUpstreamValues: lastSyncedUpstreamValues ?? [],
          })),
          lastSyncedUpstreamValues
        ).map(({lastSyncedUpstreamValues}) => lastSyncedUpstreamValues);

        const newTaskTable = setRowsInTaskTable(currentTaskTable, tasksToSet, lastSyncedUpstreamValuesToSet);
        const rowsToPreserve = currentTaskTable.rows.slice(startIdxToPreserve);
        lastSyncedUpstreamValuesToSet.push(
          ...rowsToPreserve.map(({lastSyncedUpstreamValues}) => lastSyncedUpstreamValues ?? [])
        );
        newTaskTable.rows.push(...rowsToPreserve);
        const allServerTasks = [
          ...tasksToSet,
          ...(startIdxToPreserve < allTasks.length ? allTasks.slice(startIdxToPreserve) : []),
        ];

        for (let idx = 0; idx < newTaskTable.rows.length; idx++) {
          if (idx < startIdxToPreserve) {
            newTaskTable.rows[idx].isFinalized = true;
          } else {
            newTaskTable.rows[idx].isFinalized = currentTaskTable.rows[idx].isFinalized;
          }
          newTaskTable.rows[idx].lastSyncedUpstreamValues = lastSyncedUpstreamValuesToSet[idx];
        }
        setTabState(allServerTasks, newTaskTable);
        setStructuredTableTogglableStatus(newTaskTable.cid, StructuredTableEditStatus.EDITING);
        throw e;
      }

      const {newAllTasks, newLastSyncedUpstreamValues} = getNewTaskData(
        submittedTasks,
        lastSyncedUpstreamValues
      );
      setTabState(newAllTasks, setRowsInTaskTable(taskTable, newAllTasks, newLastSyncedUpstreamValues, true));
    } else {
      const response = await bulkCreateOrUpdateProcessTasks({
        tasks: tasksToSubmit.map(({submission}) => submission),
        showLoadingScreen: true,
        totalTasksInTable: tasks.length,
        dataModsHeader,
      });
      const {newAllTasks, newLastSyncedUpstreamValues} = getNewTaskData(
        response.tasks,
        getLastSyncedUpstreamValuesWithIdFromResponse(tasksToSubmit, response)
      );

      const newTaskTable = setRowsInTaskTable(taskTable, newAllTasks, newLastSyncedUpstreamValues, true);
      setTabState(newAllTasks, newTaskTable);
    }
  };

  const tableSubmissionUpdates = await getStructuredTableCellDiffOnSubmission(
    [computedTaskTable],
    [taskTable],
    [getStructuredTableAdapters()[StructuredTableKind.PROCESS_TASK] as ProcessTaskStructuredTableAdapter]
  );

  const itemChangeCounts = getDataModsCountForTableSubmission(
    [taskTable],
    tableSubmissionUpdates.tableSubmissionUpdates
  );
  const apiIds = [...new Set(itemChangeCounts.cell)];
  const itemChangeCount = mapObject(itemChangeCounts, (itemIds: Array<string>) => itemIds.length);
  const counts = itemCountsForChangeSummary(itemChangeCount);
  const changeSummary = counts ? `${counts} will be modified.` : '';

  await submitTasksWithConfirmation({
    taskSchemaId: getTaskSchema(taskTable.schemas)!.id,
    tasks: tasks.map((task, idx) => ({
      ...task,
      oldStatusId: getLastSyncedUpstreamTaskStatus(taskTable.rows[idx])?.id ?? '',
    })),

    onRequestSubmit: (): Promise<void> =>
      asyncBulkUpdateProcessWithDataMods(
        apiIds,
        async (dataModsHeader?: DataModificationsHeader): Promise<void> => {
          await onSubmitProcessDetails(dataModsHeader);
        },
        ActionOrigin.WORKFLOWS_TASK_GROUP_WORKFLOW_TASK_TABLE,
        changeSummary
      ),
  });
}

export async function submitOutputTableHandler({
  process,
  outputTable,
  computedOutputTable,
  taskTable,
  setTaskTable,
  setOutputTable,
}: {
  process: ProcessGql;
  outputTable: ProcessOutputStructuredTable;
  computedOutputTable: EvaluatedTable;
  taskTable: ProcessTaskStructuredTable;
  setTaskTable: (table: ProcessTaskStructuredTable) => void;
  setOutputTable: (table: ProcessOutputStructuredTable) => void;
}): Promise<void> {
  const sourceType = `workspace_tab_${getSourceTypeForStructuredTable(outputTable)}_direct`;
  assert((outputTable.columns?.length ?? 0) > 1, 'Output table must exist and have columns');
  if (doesEvaluatedTableHaveAnyErrors(computedOutputTable, EVALUATED_TABLE_ERROR_OPTIONS)) {
    return;
  }
  const outputs = outputTableToOutputs({
    table: outputTable,
    evaluatedTable: computedOutputTable,
    sourceType,
    actionOrigin: ActionOrigin.WORKFLOWS_TASK_GROUP_WORKFLOW_OUTPUT_TABLE,
    actionMethod: ActionMethod.BUTTON_CLICK,
    creationOriginId: process.id,
  });

  const tableSubmissionUpdates = await getStructuredTableCellDiffOnSubmission(
    [computedOutputTable],
    [outputTable],
    [getStructuredTableAdapters()[StructuredTableKind.PROCESS_OUTPUT] as ProcessTaskStructuredTableAdapter]
  );

  const itemChangeCounts = getDataModsCountForTableSubmission(
    [outputTable],
    tableSubmissionUpdates.tableSubmissionUpdates
  );
  const apiIds = [...new Set(itemChangeCounts.cell)];
  const itemChangeCount = mapObject(itemChangeCounts, (itemIds: Array<string>) => itemIds.length);
  const counts = itemCountsForChangeSummary(itemChangeCount);
  const changeSummary = counts ? `${counts} will be modified.` : '';

  await submitOutputsWithConfirmation({
    outputs,
    onRequestSubmit: ({processOutputs, processTasks}) =>
      asyncBulkUpdateProcessWithDataMods(
        apiIds,
        async (dataModsHeader?: DataModificationsHeader): Promise<void> => {
          await submitOutputsAndCorrespondingTasksPostConfirmation({
            processOutputs,
            processTasks: processTasks?.map((task) => ({
              ...task,
              // The task update is being triggered from the output table.
              sourceType,
              actionOrigin: ActionOrigin.WORKFLOWS_TASK_GROUP_WORKFLOW_OUTPUT_TABLE,
              actionMethod: ActionMethod.BUTTON_CLICK,
            })),
            process,
            outputTable,
            computedOutputTable,
            taskTable,
            setTaskTable,
            setOutputTable,
            dataModsHeader,
          });
        },
        ActionOrigin.WORKFLOWS_DASHBOARD,
        changeSummary
      ),
  });
}

export function ProcessDetails(props: ProcessDetailsProps): JSX.Element {
  const {process, selectors} = props;
  const selectorsById = useMemo(() => getSelectorsById(selectors), [selectors]);
  const buildTaskTable = (process: ProcessGql): ProcessTaskStructuredTable =>
    createTaskTable({
      process,
      tableLocation: ProcessTableLocation.WORKSPACE_TAB,
      selectorsById,
      locked: !isFeatureEnabled({feature: ProcessesFeature.PROCESSES_EDIT}) || process.tasks.length === 0,
      isInFlowchartNode: process.rootProcess != null,
    });
  const buildOutputTable = (process: ProcessGql): ProcessOutputStructuredTable | null =>
    hasOutputSchema(process)
      ? createOutputTable({
          process,
          metadata: {tableLocation: ProcessTableLocation.WORKSPACE_TAB},
          selectorsById,
          locked: !isFeatureEnabled({feature: ProcessesFeature.PROCESSES_EDIT}),
        })
      : null;
  const [allTasks, setAllTasks] = useState<Array<ProcessTask>>(process.tasks);
  const [taskTable, setTaskTable] = useState(buildTaskTable(process));
  const [outputTable, setOutputTable] = useState(buildOutputTable(process));
  const [computedTaskTable, setComputedTaskTable] = useState<EvaluatedTable>();
  const [computedOutputTable, setComputedOutputTable] = useState<EvaluatedTable | null>();

  const enableEditsOnOutputTable = process.processTaskSchema.executionType === ProcessExecutionType.DIRECT;

  const showDirectExecutionNoOutputBanner = isDirectExecutionTaskWithNoOutputSchema(
    process.processTaskSchema
  );
  const showFixFailedMappingsBanner = process.hasTasksWithFailedMappings;

  useEffect(() => {
    setAllTasks(process.tasks);
    setTaskTable(buildTaskTable(process));
    setOutputTable(buildOutputTable(process));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [process]);

  const updateHandlerTaskTable = (
    structuredTable: ProcessTaskStructuredTable,
    lastSyncedArchivePurposeByRow?: Array<string | null | undefined>
  ): void => {
    setTaskTable(structuredTable);
    if (lastSyncedArchivePurposeByRow) {
      const tasksWithArchivePurpose = props.process.tasks.map((task, idx) =>
        attachArchivePurpose(task, lastSyncedArchivePurposeByRow[idx])
      );
      updateProcessDetailsQueryCache(props.process.id, {
        tasks: tasksWithArchivePurpose,
      });
    }
    analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.CLICKED_TASK_TABLE_EDIT, {
      process_client_origin: process.processTaskSchema.clientOrigin,
    });
  };

  const updateHandlerOutputTable = (
    structuredTable: ProcessOutputStructuredTable,
    lastSyncedArchivePurposeByRow?: Array<string | null | undefined>
  ): void => {
    setOutputTable(structuredTable);
    if (lastSyncedArchivePurposeByRow) {
      const outputsWithArchivePurpose = props.process.outputs.map((output, idx) =>
        attachArchivePurpose(output, lastSyncedArchivePurposeByRow[idx])
      );
      updateProcessDetailsQueryCache(props.process.id, {
        outputs: outputsWithArchivePurpose,
      });
    }
    analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.CLICKED_OUTPUT_TABLE_EDIT, {
      process_client_origin: props.process.processTaskSchema.clientOrigin,
    });
  };

  return (
    <>
      {showDirectExecutionNoOutputBanner && (
        <div className="u-small-padding-bottom">
          <InlineBanner
            bannerType={InlineBannerType.INFO}
            text="To execute this task group, edit the tasks' details and statuses in the table below"
          />
        </div>
      )}
      <div className={css(styles.headingWrapper)}>
        <div>
          <ProcessNameEditor process={props.process} isEditable={props.isEditable} />
          <TaskStatusSummary tasks={allTasks} archiveRecord={process.archiveRecord} />
        </div>
        {props.isEditable && (
          <div className="u-small-padding-right">
            <ProcessActionToolbar
              process={process}
              forceRefetch={props.refetch}
              onTasksOrOutputsChange={(
                tasksToRefresh?: Array<ProcessTaskGraphQL>,
                outputsToRefresh?: Array<ProcessOutputGraphQL>
              ) => {
                updateProcessDetailsQueryCache(props.process.id, {
                  tasks: tasksToRefresh,
                  outputs: outputsToRefresh,
                  mergeTasks: true,
                  mergeOutputs: true,
                });
              }}
            />
          </div>
        )}
      </div>
      {showFixFailedMappingsBanner && (
        <div className={classNames('u-small-padding-bottom', css(styles.failedMappingsWrapper))}>
          <InlineBanner
            bannerType={InlineBannerType.ERROR}
            text="Fix mapping errors to continue this workflow."
            action={{
              actionType: InlineBannerActionType.CUSTOM,
              actionText: 'View & fix error(s)',
              actionOnClick: () => {
                navigateToURL(getFailedMappingTasksFilteredByProcessesUrl(process.id));
              },
            }}
          />
        </div>
      )}
      <div className={css(styles.detailsRow)}>
        <Row>
          <ProcessDetailsColumn title="Schema">
            <span>
              {process.processTaskSchema.name}
              {process.processTaskSchema.archiveRecord && ' (archived)'}
            </span>
          </ProcessDetailsColumn>
          <ProcessDetailsColumn title="Execution type">
            <span>
              {processExecutionTypeDisplayName(
                process.processTaskSchema.clientOrigin,
                process.processTaskSchema.executionType
              )}
            </span>
          </ProcessDetailsColumn>
          {props.process.rootProcess && (
            <ProcessDetailsColumn title="Flowchart">
              <RootProcessLink rootProcess={props.process.rootProcess} />
            </ProcessDetailsColumn>
          )}
          <ProcessDetailsColumn title="Watchers">
            <span className={css(styles.watcherListWrapper)}>
              <ProcessWatchersField
                isLoading={props.process?.id == null}
                watchers={props.process?.watchers ?? []}
                onChange={async (changedValue) => {
                  const {watchers} = await updateProcess({
                    id: props.process.id,
                    watcherIds: changedValue.map(({id}) => id),
                    sourceType: 'details',
                    actionOrigin: ActionOrigin.WORKFLOWS_TASK_GROUP_PAGE,
                    actionMethod: ActionMethod.DIRECT_EDIT,
                    eventGroupId: makeAnalyticsEventId(),
                  });
                  updateProcessDetailsQueryCache(props.process.id, {watchers});
                  notifications.success('Updated watchers');
                }}
                isDisabled={
                  !props.isEditable ||
                  !isFeatureEnabled({feature: ProcessesFeature.PROCESSES_EDIT, item: process})
                }
              />
            </span>
          </ProcessDetailsColumn>
          {BenchlingConfig.REQUESTS_V2_ENABLE && process.requestV2Submission != null && (
            <ProcessDetailsColumn title="Request">
              <RequestV2SubmissionChip
                id={getAccessibleOrInaccessibleId(process.requestV2Submission)}
                size={ChipSize.SMALL}
              />
            </ProcessDetailsColumn>
          )}
        </Row>
      </div>
      <div className={css(styles.detailsRow)}>
        <Row>
          <Col sm={12}>
            <div className="metadata-section">
              <div className="metadata-header">Instructions</div>
              <TaskGroupInstructions
                lines={DefaultTaskGroupInstructionsLines.WORKSPACE_TAB}
                textValue={props.process.processTaskSchema.taskGroupInstructions}
                actionOrigin={ActionOrigin.WORKFLOWS_TASK_GROUP_INSTRUCTIONS_HEADER}
                clientOrigin={props.process.processTaskSchema.clientOrigin}
              />
            </div>
          </Col>
        </Row>
      </div>

      {taskTable && (
        <ToggleEditStructuredTableOutsideNotebook
          isEditable={props.isEditable && (process.tasks.length > 0 || process.rootProcess == null)}
          structuredTable={taskTable}
          update={updateHandlerTaskTable}
          submit={async () => {
            await submitTaskTableHandler({
              process,
              allTasks,
              taskTable,
              computedTaskTable: computedTaskTable!,
              setTaskTable,
              setAllTasks,
            });
          }}
          revert={(table) => {
            setTaskTable(table);
          }}
          disableMaximizedTables={false}
          updateComputedTable={setComputedTaskTable}
          enableFormulas={true}
          useWithTableViews={true}
        />
      )}
      {outputTable && taskTable && (
        <ToggleEditStructuredTableOutsideNotebook
          isEditable={props.isEditable && enableEditsOnOutputTable}
          structuredTable={outputTable}
          outsideNotebookContext={{
            taskIds: allTasks.map(({id}) => id),
          }}
          update={updateHandlerOutputTable}
          submit={async () => {
            await submitOutputTableHandler({
              process: props.process,
              outputTable,
              taskTable,
              computedOutputTable: computedOutputTable!,
              setTaskTable,
              setOutputTable,
            });
          }}
          revert={(table) => {
            setOutputTable(table);
          }}
          disableMaximizedTables={false}
          updateComputedTable={setComputedOutputTable}
          enableFormulas={true}
          useWithTableViews={true}
        />
      )}
    </>
  );
}

interface QueryingProcessDetailsProps {
  processId: string;
}

export default function QueryingProcessDetails(props: QueryingProcessDetailsProps): JSX.Element {
  return (
    <BenchlingQuery<FetchSelectorsByIdQuery, FetchSelectorsByIdQueryVariables>
      query={FETCH_SELECTORS_BY_ID_QUERY}
      variables={{includeArchived: true}}
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {(selectorQueryData) => {
        const selectors = selectorQueryData.schemaFieldSelectors;
        return (
          <BenchlingQuery<ProcessDetailsQuery, ProcessDetailsQueryVariables>
            query={PROCESS_DETAILS_QUERY}
            variables={{processId: props.processId}}
            fetchPolicy="cache-and-network"
            renderCachedDataWhenRefetching={true}
            renderLoading={() => <CenteredHorizontalThrobber />}
          >
            {(data, {refetch}) => {
              const {process} = data;
              return !isInaccessible(process) ? (
                <ProcessRealtimeUpdateSubscriptionWrapper
                  apiId={process.id}
                  messageFilter="no_filter"
                  refetch={refetch}
                  refetchOnMessagesFromSameClient={true}
                  hasIncompleteDataShouldRefetch={
                    process.isMappingInProgressFromProcess || process.isMappingInProgressToProcess
                  }
                >
                  <div className={css(styles.structedTableViewPanelWrapper)}>
                    <StructuredTableViewPanel>
                      <div className={classNames(css(styles.processDetailsWrapper), 'scrollbar-override')}>
                        <ProcessDetails
                          process={process}
                          selectors={selectors}
                          refetch={refetch}
                          isEditable={
                            getProcessPlatformFeatureConfig(
                              ProcessPlatformFeatureType.CAN_EDIT_TASK_GROUPS,
                              process.processTaskSchema.executionType,
                              process.processTaskSchema.name
                            ) && process.permissions.writable
                          }
                        />
                      </div>
                    </StructuredTableViewPanel>
                  </div>
                </ProcessRealtimeUpdateSubscriptionWrapper>
              ) : (
                <WorkspaceItemInaccessibleGuard inaccessibleInfo={process} modelType="process" />
              );
            }}
          </BenchlingQuery>
        );
      }}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  nameWrapper: {
    display: 'flex',
    width: '100%',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  headingWrapper: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  failedMappingsWrapper: {
    width: '100%',
  },
  detailsRow: {
    marginTop: '20px',
  },
  iconStyle: {
    marginLeft: '5px',
    color: GRAY_DARK,
  },
  watcherListWrapper: {
    marginTop: '-6px',
    display: 'flex',
  },
  structedTableViewPanelWrapper: {
    height: '100%',
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
  },
  processDetailsWrapper: {
    height: '100%',
    overflow: 'auto',
    padding: '30px 20px',
  },
});
